/**
 * model-card-grid.tsx — Chọn model DÙNG CHUNG cho màn Nguồn AI và lượt AI (bước 2 "Chọn model của tổ chức") và mục Bộ não
 * của nhân viên AI ("Dùng model khác cho nhân viên này"). Hai tầng cho đỡ rối:
 *   1. Hàng THẺ NHÀ CUNG CẤP (xếp ngang, tự xuống dòng) — mỗi thẻ một nhà cung cấp: logo, tên, "N model",
 *      eCDP "từ ~X credit/lượt" hoặc trạng thái key (Key của tôi), nhãn "Đang dùng" nếu model đang dùng thuộc nhà cung cấp đó.
 *   2. Bấm một thẻ → Ô CHỌN (dropdown) model của nhà cung cấp đó: "tên · hạng · credit/lượt", dòng khoá theo gói 🔒 không chọn được.
 * Chọn model = `onSelect` — KHÔNG lưu; nơi dùng quyết định (khung Cài đặt model / lưu nháp). Logic thuần:
 * model-card-grid-lib.ts.
 */
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { aiProviderLogo } from '@/lib/ai-providers'
import type { AiModelEntry, ByokState } from '@/lib/ai-credits'
import { cn } from '@/lib/utils'
import {
  groupByProvider, initialProvider, modelCardView, modelOptionLabel, orderModels, providerCardView, sameModel, type ModelRef,
} from './model-card-grid-lib'

export interface ModelCardGridProps {
  models: AiModelEntry[]
  /** Model đang dùng (nhãn "Đang dùng"). */
  current?: ModelRef | null
  /** Model đang chọn (viền màu chính). */
  selected?: ModelRef | null
  onSelect: (m: AiModelEntry) => void
  /** Trạng thái key của nhà cung cấp — tag "Có key / Cần key / Key lỗi" cho Key của tôi. */
  byokOf?: (provider: string) => ByokState | null | undefined
  names?: Array<{ provider: string; name: string }> | null
  /** Thẻ nhỏ hơn (chỗ hẹp, vd mục Bộ não). */
  compact?: boolean
  disabled?: boolean
  ariaLabel?: string
  currentLabel?: string
}

export function ModelCardGrid({
  models, current, selected, onSelect, byokOf, names, compact = false, disabled, ariaLabel: ariaLabelProp, currentLabel: currentLabelProp,
}: ModelCardGridProps) {
  const { t } = useTranslation('aiComp')
  const ariaLabel = ariaLabelProp ?? t('modelGrid.pick')
  const currentLabel = currentLabelProp ?? t('modelGrid.inUse')
  const groups = useMemo(() => groupByProvider(orderModels(models)), [models])
  const [open, setOpen] = useState<string | null>(() => initialProvider(groups, selected, current))
  // Danh sách đổi (đổi nguồn) mà nhà cung cấp đang mở không còn → mở lại theo model đang chọn / đang dùng.
  useEffect(() => {
    if (!open || !groups.some((g) => g.provider === open)) setOpen(initialProvider(groups, selected, current))
  }, [groups, open, selected, current])
  const openGroup = groups.find((g) => g.provider === open) ?? null

  return (
    <div className="space-y-3">
      <div
        role="tablist" aria-label={t('modelGrid.providerAria', { label: ariaLabel })}
        className={cn('grid gap-2', compact ? 'grid-cols-[repeat(auto-fill,minmax(150px,1fr))]' : 'grid-cols-[repeat(auto-fill,minmax(180px,1fr))]')}
      >
        {groups.map((g) => {
          const v = providerCardView(g.provider, g.models, { byokOf, current, names })
          const isOpen = g.provider === open
          const logo = aiProviderLogo(g.provider)
          return (
            <button
              key={g.provider}
              type="button"
              role="tab"
              aria-selected={isOpen}
              disabled={disabled}
              onClick={() => setOpen(g.provider)}
              className={cn(
                'flex min-w-0 flex-col gap-1.5 rounded-lg border text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                compact ? 'p-2.5' : 'p-3',
                isOpen ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50',
                v.current && !isOpen && 'border-success ring-1 ring-success/60',
              )}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                {logo && <img src={logo} alt="" aria-hidden draggable={false} className="h-5 w-5 shrink-0 rounded-[3px] bg-white object-contain p-px" />}
                <span className={cn('min-w-0 truncate font-semibold', compact ? 'text-xs' : 'text-sm')}>{v.name}</span>
                {v.current && <Badge variant="success" className="ml-auto gap-1 whitespace-nowrap text-[10px]"><Check className="h-3 w-3" /> {currentLabel}</Badge>}
              </span>
              <span className="flex flex-wrap gap-1">
                {v.tags.map((tag) => (
                  <Badge key={tag.kind} variant={tag.tone} className="whitespace-nowrap px-1.5 py-0 text-[10px] font-normal">{tag.label}</Badge>
                ))}
              </span>
            </button>
          )
        })}
      </div>

      {openGroup && (() => {
        const views = openGroup.models.map((m) => ({ m, v: modelCardView(m, { byokOf, current, names }) }))
        // Chưa bấm chọn → ô hiện sẵn model đang dùng (nếu thuộc nhà cung cấp này).
        const chosen = views.find((x) => sameModel(x.m, selected)) ?? views.find((x) => sameModel(x.m, current)) ?? null
        const selCls = cn(
          'w-full rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-1 focus:ring-ring disabled:opacity-60 dark:bg-card',
          compact ? 'py-1.5' : 'py-2',
        )
        return (
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{t('modelGrid.modelOf', { name: providerCardView(openGroup.provider, openGroup.models, { names }).name })}</span>
            <select
              className={selCls}
              aria-label={ariaLabel}
              disabled={disabled}
              value={chosen ? chosen.v.key : ''}
              onChange={(e) => {
                const hit = views.find((x) => x.v.key === e.target.value)
                if (hit) onSelect(hit.m)
              }}
            >
              <option value="" disabled>{t('modelGrid.placeholder')}</option>
              {views.map(({ v }) => (
                <option key={v.key} value={v.key} disabled={!!v.lock}>{modelOptionLabel(v, currentLabel.toLowerCase())}</option>
              ))}
            </select>
          </label>
        )
      })()}
    </div>
  )
}
